import React, { useState, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLexware } from '../context/LexwareContext';
import StatsCards from '../components/dashboard/StatsCards';
import RevenueChart from '../components/dashboard/RevenueChart';
import ProfitChart from '../components/dashboard/ProfitChart';
import TodaysTasks from '../components/dashboard/TodaysTasks';
import RecentActivities from '../components/dashboard/RecentActivities';
import ChurnListModal from '../components/dashboard/ChurnListModal';
import CustomerDetailsModal from '../components/customers/CustomerDetailsModal';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { differenceInCalendarDays } from 'date-fns';

const { FiRefreshCw, FiDownloadCloud } = FiIcons;

function Dashboard() {
  const { data, refreshData, saveToCloud, isLoading } = useLexware();
  const [selectedChurnSegment, setSelectedChurnSegment] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const churnSegments = useMemo(() => {
    const customers = data.customers || [];
    const sales = data.sales || [];
    const today = new Date();
    const customerLastOrder = new Map();
    const customerOrders = new Map();

    sales.forEach(sale => {
      const customerId = sale['Kundenummer'];
      const orderDate = new Date(sale['RG-Datum']);
      if (!customerLastOrder.has(customerId) || orderDate > customerLastOrder.get(customerId)) customerLastOrder.set(customerId, orderDate);
      if (!customerOrders.has(customerId)) customerOrders.set(customerId, []);
      customerOrders.get(customerId).push(orderDate);
    });

    customerOrders.forEach(dates => dates.sort((a, b) => a - b));

    const segments = { observation: [], delayed: [], critical: [], inactive: [], legacy_critical: [], legacy_warning: [] };

    customers.forEach(customer => {
      const lastOrderDate = customerLastOrder.get(customer.id);
      if (!lastOrderDate) return;
      const daysSinceOrder = differenceInCalendarDays(today, lastOrderDate);
      const orders = customerOrders.get(customer.id);
      let avgCycle = 30;
      if (orders && orders.length > 1) {
        let totalDays = 0;
        for (let i = 1; i < orders.length; i++) totalDays += differenceInCalendarDays(orders[i], orders[i-1]);
        avgCycle = Math.round(totalDays / (orders.length - 1));
      }
      avgCycle = Math.max(14, Math.min(365, avgCycle));
      const daysOverdue = Math.max(0, daysSinceOrder - avgCycle);
      const cycleMissedFactor = daysOverdue / avgCycle;
      const customerWithStats = { ...customer, lastOrderDate, daysSinceOrder, avgCycle, daysOverdue };

      if (daysOverdue > 0) {
        if (cycleMissedFactor > 3) segments.inactive.push(customerWithStats);
        else if (cycleMissedFactor >= 2) segments.critical.push(customerWithStats);
        else if (daysOverdue > 10) segments.delayed.push(customerWithStats);
        else segments.observation.push(customerWithStats);
      }
      if (daysSinceOrder > 180) segments.legacy_critical.push(customerWithStats);
      else if (daysSinceOrder >= 90) segments.legacy_warning.push(customerWithStats);
    });

    Object.keys(segments).forEach(key => segments[key].sort((a, b) => b.daysOverdue - a.daysOverdue));
    return segments;
  }, [data.customers, data.sales]);

  const handleOpenCustomer = useCallback((customer) => {
    const fullCustomer = data.customers.find(c => c.id === customer.id) || customer;
    // Set customer and close churn list in one go
    setSelectedCustomer(fullCustomer);
    setSelectedChurnSegment(null);
  }, [data.customers]);

  const handleOpenChurnList = useCallback((segmentKey) => {
    const config = {
      'observation': { title: 'Beobachtung (Überfällig < 10 Tage)', data: churnSegments.observation },
      'delayed': { title: 'Verzögerung (> 10 Tage)', data: churnSegments.delayed },
      'critical': { title: 'Kritisch (2x-3x Zyklus verpasst)', data: churnSegments.critical },
      'inactive': { title: 'Inaktiv (> 3x Zyklus verpasst)', data: churnSegments.inactive },
      'legacy_critical': { title: 'Kritische Kunden (> 180 Tage)', data: churnSegments.legacy_critical },
      'legacy_warning': { title: 'Warnung (90 - 180 Tage)', data: churnSegments.legacy_warning }
    };

    const selection = config[segmentKey] || (segmentKey === 'critical_legacy' ? config.legacy_critical : segmentKey === 'warning_legacy' ? config.legacy_warning : null);
    if (selection) setSelectedChurnSegment({ title: selection.title, customers: selection.data });
  }, [churnSegments]);

  return (
    <div className="space-y-6">
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="flex justify-between items-center bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
        <div><h1 className="text-3xl font-black text-gray-900 tracking-tight">Dashboard</h1><p className="text-gray-500 font-medium mt-1">Willkommen zurück! Hier ist der aktuelle Status.</p></div>
        <div className="flex gap-3">
          <button onClick={() => saveToCloud(true)} className="flex items-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-bold hover:bg-gray-200 transition-all cursor-pointer"><SafeIcon icon={FiDownloadCloud} /> Backup</button>
          <button onClick={refreshData} disabled={isLoading} className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-all shadow-lg shadow-blue-200 disabled:opacity-50 cursor-pointer"><SafeIcon icon={FiRefreshCw} className={isLoading ? "animate-spin" : ""} />{isLoading ? 'Lädt...' : 'Aktualisieren'}</button>
        </div>
      </motion.div>

      <StatsCards onOpenChurnList={handleOpenChurnList} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <RevenueChart />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <ProfitChart onOpenCustomer={handleOpenCustomer} legacyChurn={{ critical: churnSegments.legacy_critical, warning: churnSegments.legacy_warning }} />
            <RecentActivities />
          </div>
        </div>
        <div className="lg:col-span-1"><TodaysTasks onTaskClick={handleOpenCustomer} /></div>
      </div>

      <AnimatePresence mode="wait">
        {selectedChurnSegment && (
          <ChurnListModal 
            key={`churn-modal-${selectedChurnSegment.title}`}
            title={selectedChurnSegment.title} 
            customers={selectedChurnSegment.customers} 
            onClose={() => setSelectedChurnSegment(null)}
            onSelectCustomer={handleOpenCustomer}
          />
        )}
      </AnimatePresence>

      <AnimatePresence mode="wait">
        {selectedCustomer && (
          <CustomerDetailsModal 
            key={`customer-details-${selectedCustomer.id}`}
            customer={selectedCustomer} 
            onClose={() => setSelectedCustomer(null)} 
          />
        )}
      </AnimatePresence>
    </div>
  );
}

export default Dashboard;